import { useEffect, useRef, useState } from "react";
import { searchPlaces } from "../../../services/mapbox";
import "./AddressAutocomplete.css";

/**
 * Address search backed by the Mapbox Geocoding API — the replacement for the
 * Google `PlacesAutocomplete` render-prop component.
 *
 * The important difference: every Mapbox suggestion already carries its own
 * coordinates, so `onSelect` hands the caller a complete place and no second
 * geocoding round trip is needed.
 *
 * @param {string} value                 controlled input text
 * @param {(text:string)=>void} onChange
 * @param {(place)=>void} onSelect       fired when a suggestion is chosen
 * @param {{lat,lng}} [proximity]        bias results near the customer
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  proximity,
  placeholder = "Search places…",
  className = "",
  autoFocus = false,
  children,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const wrapRef = useRef(null);
  // Set while a suggestion is being applied, so echoing the chosen text back
  // through `value` doesn't immediately re-open the dropdown.
  const justPickedRef = useRef(false);

  useEffect(() => {
    if (justPickedRef.current) {
      justPickedRef.current = false;
      return;
    }
    const term = String(value || "").trim();
    if (term.length < 2) {
      setSuggestions([]);
      setError("");
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        setError("");
        const results = await searchPlaces(term, {
          proximity,
          signal: controller.signal,
        });
        setSuggestions(results);
        setOpen(true);
      } catch (e) {
        if (e.name !== "AbortError") {
          setSuggestions([]);
          setError(e.message || "Address search is unavailable.");
        }
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // `proximity` is a fresh object each render in some callers; only the text
    // should drive a new search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Close when clicking away.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (place) => {
    justPickedRef.current = true;
    onChange?.(place.address);
    setSuggestions([]);
    setOpen(false);
    onSelect?.(place);
  };

  return (
    <div className="mb-autocomplete" ref={wrapRef}>
      <div className="mb-autocomplete-field">
        <input
          type="text"
          value={value || ""}
          autoFocus={autoFocus}
          placeholder={placeholder}
          className={className || "mb-autocomplete-input"}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
        />
        {children}
      </div>

      {open && (loading || error || suggestions.length > 0) && (
        <div className="mb-autocomplete-menu">
          {loading && <div className="mb-autocomplete-note">Searching…</div>}
          {error && !loading && (
            <div className="mb-autocomplete-note mb-autocomplete-error">{error}</div>
          )}
          {!loading &&
            !error &&
            suggestions.map((place) => (
              <button
                key={place.id}
                type="button"
                className="mb-autocomplete-item"
                onClick={() => pick(place)}
              >
                {place.address}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
